/**
 * Feedback service for handling feedback and bug report submissions
 * Uses backend API with Cloudflare Turnstile verification and EmailJS
 */

export type FeedbackCategory = 'feedback' | 'bug' | 'feature';

export interface FeedbackData {
  category: FeedbackCategory;
  name?: string;
  email?: string;
  subject: string;
  message: string;
  currentRoute?: string;
  userAgent?: string;
  turnstileToken?: string; // Cloudflare Turnstile token
}

/**
 * Get backend API URL from environment variables
 * In production (Vercel), uses relative paths for serverless functions
 * In development, uses the Express server on localhost:3001
 */
export function getApiUrl(): string {
  // In production (Vercel), use relative paths for serverless functions
  if (process.env.NODE_ENV === 'production') {
    return ''; // Relative path - will use Vercel serverless functions
  }
  // In development, use the Express server
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
}

/**
 * Format feedback data into a readable email body
 */
export function formatFeedbackEmail(data: FeedbackData): string {
  const categoryLabels: Record<FeedbackCategory, string> = {
    feedback: 'General Feedback',
    bug: 'Bug Report',
    feature: 'Feature Request',
  };

  let emailBody = `Category: ${categoryLabels[data.category]}\n\n`;
  
  if (data.name) {
    emailBody += `Name: ${data.name}\n`;
  }
  
  if (data.email) {
    emailBody += `Email: ${data.email}\n`;
  }
  
  emailBody += `\nSubject: ${data.subject}\n\n`;
  emailBody += `Message:\n${data.message}\n\n`;
  
  if (data.currentRoute) {
    emailBody += `---\nCurrent Page: ${data.currentRoute}\n`;
  }
  
  if (data.userAgent) {
    emailBody += `User Agent: ${data.userAgent}\n`;
  }
  
  emailBody += `Timestamp: ${new Date().toISOString()}\n`;
  
  return emailBody;
}

/**
 * Submit feedback via backend API with Cloudflare Turnstile verification
 * Returns a Promise that resolves on success or rejects on error
 */
export async function submitFeedback(data: FeedbackData): Promise<void> {
  const apiUrl = getApiUrl();

  // Validate required fields
  if (!data.subject || !data.message) {
    throw new Error('Subject and message are required');
  }

  // Validate Turnstile token if provided
  if (!data.turnstileToken) {
    throw new Error('Turnstile verification is required');
  }

  try {
    const response = await fetch(`${apiUrl}/api/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || 'Failed to submit feedback');
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to submit feedback. Please try again later.');
  }
}
